import type * as MaplibreGl from "maplibre-gl";

import type { LocatedCity, ML } from "@/components/atlas-camera";

/**
 * The city markers: one bar per city, positioned by MapLibre's own `Marker` and lit by the selection.
 *
 * The scene controller's one piece that owns DOM of its own — a bar element plus a marker per city,
 * reconciled against the payload rather than rebuilt — so it lives beside the other layer modules
 * (`atlas-planes`, `atlas-labels`, `atlas-cards`) instead of inside the controller, which is the
 * file's own size ceiling and not a place to grow a fifth layer.
 */

/** One city's bar: the element the stylesheet paints, and the marker that projects it. */
export type CityMarker = { name: string; element: HTMLDivElement; marker: MaplibreGl.Marker };

export interface AtlasMarkers {
  /** The live bars, as the pointer's cards read them: the names a card must stay clear of. */
  list: () => readonly CityMarker[];
  /**
   * Reconcile the bars against a payload: a survivor takes its city's new coordinates, a marker whose
   * city left the payload goes with it, and a city that arrived gets one. The selection is re-applied
   * on every pass, because a rebuilt element arrives unmarked.
   */
  reconcile: (cities: readonly LocatedCity[]) => void;
  /** Light one city's bar, or none: the marker the directory's own selection is on. */
  setActive: (name: string | null) => void;
  dispose: () => void;
}

/**
 * Create the marker layer over an already-constructed map. `vendor` is the same vendored module the
 * map was built from — a `Marker` from one copy of MapLibre cannot project onto another's canvas.
 */
export const createAtlasMarkers = (map: MaplibreGl.Map, vendor: ML): AtlasMarkers => {
  let markers: CityMarker[] = [];
  let active: string | null = null;

  return {
    list: () => markers,
    reconcile: (cities) => {
      const wanted = new Map(cities.map((city) => [city.name, city]));
      // One pass over the live markers: a survivor takes its city's new coordinates, and a marker
      // whose city left the payload goes with it.
      markers = markers.filter((entry) => {
        const city = wanted.get(entry.name);
        if (!city) {
          entry.marker.remove();
          return false;
        }
        entry.marker.setLngLat([city.lng, city.lat]);
        return true;
      });
      for (const [name, city] of wanted) {
        if (markers.some((entry) => entry.name === name)) continue;
        const element = document.createElement("div");
        element.className = "noko-marker";
        element.innerHTML = '<span class="noko-marker-bar"></span>';
        markers.push({
          name,
          element,
          marker: new vendor.Marker({ element, anchor: "left" })
            .setLngLat([city.lng, city.lat])
            .addTo(map),
        });
      }
      for (const entry of markers) {
        entry.element.classList.toggle("is-active", entry.name === active);
      }
    },
    setActive: (name) => {
      active = name;
      for (const entry of markers) {
        entry.element.classList.toggle("is-active", entry.name === name);
      }
    },
    dispose: () => {
      for (const entry of markers) entry.marker.remove();
      markers = [];
    },
  };
};
